import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { useAuth } from '../../contexts/AuthContext';
import {
  acknowledgeSrhtChecklistCompletion,
  exportSrhtChecklistPdf,
  finalizeSrhtChecklist,
  getMySrhtChecklist,
  getSrhtChecklistAdminDetail,
  getSrhtChecklistAdminOpenList,
  getSrhtChecklistInstructors,
  saveMySrhtChecklistItem,
} from '../../api/srhtChecklist';
import { downloadPdfResponse, extractPdfErrorMessage } from '../../utils/pdfDownload';
import { canManageSrhtChecklist } from '../../config/roleAccess';

function formatDate(value) {
  const normalized = String(value || '').trim();
  if (!normalized) {
    return '-';
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return normalized;
  }

  return parsed.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getSectionProgress(section) {
  const items = Array.isArray(section?.items) ? section.items : [];
  const totalCount = items.length;
  const completedCount = items.filter((item) => Boolean(item?.isCompleted)).length;
  return { completedCount, totalCount };
}

function ChecklistEditDialog({
  open,
  item,
  instructors,
  onClose,
  onSave,
  saving,
}) {
  const [instructorUserId, setInstructorUserId] = useState('');
  const [completedDate, setCompletedDate] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !item) {
      return;
    }
    setInstructorUserId(item.instructorUserId ? String(item.instructorUserId) : '');
    setCompletedDate(item.completedDate || '');
    setError('');
  }, [open, item]);

  const handleSave = async () => {
    if (!String(instructorUserId || '').trim()) {
      setError('Bitte einen Einweiser auswählen.');
      return;
    }
    if (!String(completedDate || '').trim()) {
      setError('Bitte ein Datum eintragen.');
      return;
    }

    setError('');
    await onSave({
      isCompleted: true,
      instructorUserId: Number(instructorUserId),
      completedDate,
    });
  };

  if (!item) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>{item.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <FormControl fullWidth size="small">
            <InputLabel id="srht-checklist-instructor-label">Einweisung durch</InputLabel>
            <Select
              labelId="srht-checklist-instructor-label"
              label="Einweisung durch"
              value={instructorUserId}
              onChange={(event) => setInstructorUserId(String(event.target.value || ''))}
            >
              {instructors.map((instructor) => (
                <MenuItem key={instructor.id} value={String(instructor.id)}>{instructor.fullName}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Datum"
            InputLabelProps={{ shrink: true }}
            value={completedDate}
            onChange={(event) => setCompletedDate(String(event.target.value || ''))}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Speichert...' : 'Speichern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MyChecklistPanel({ canFinalize }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [payload, setPayload] = useState({ checklist: null });
  const [instructors, setInstructors] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [checklistResponse, instructorsResponse] = await Promise.all([
        getMySrhtChecklist(),
        getSrhtChecklistInstructors(),
      ]);
      setPayload(checklistResponse.data || { checklist: null });
      setInstructors(Array.isArray(instructorsResponse.data?.instructors) ? instructorsResponse.data.instructors : []);
      setCompletionDialogOpen(Boolean(checklistResponse.data?.showCompletionDialog));
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Der Laufzettel konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaveItem = async (nextData) => {
    if (!editingItem?.id) {
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await saveMySrhtChecklistItem(editingItem.id, nextData);
      setPayload(response.data || { checklist: null });
      setEditingItem(null);
      setNotice('Thema gespeichert.');
      if (response.data?.showCompletionDialog) {
        setCompletionDialogOpen(true);
      }
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Die Position konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseCompletionDialog = async () => {
    setCompletionDialogOpen(false);
    try {
      await acknowledgeSrhtChecklistCompletion();
      setPayload((current) => ({
        ...current,
        checklist: current?.checklist
          ? { ...current.checklist, completionNotifiedAt: new Date().toISOString() }
          : current?.checklist,
        showCompletionDialog: false,
      }));
    } catch {
      // Ignore acknowledge errors to avoid blocking user flow.
    }
  };

  if (loading) {
    return <Alert severity="info">Laufzettel wird geladen...</Alert>;
  }

  if (!payload?.checklist) {
    return null;
  }

  const checklist = payload.checklist;
  const progress = payload.progress || { completedCount: 0, totalCount: 0, percent: 0 };
  const sections = Array.isArray(payload.sections) ? payload.sections : [];
  const isFinalized = checklist.status === 'finalized';

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
      {notice ? <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert> : null}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Laufzettel</Typography>
            <Typography variant="body2" color="text.secondary">
              Fortschritt: {progress.completedCount}/{progress.totalCount} ({progress.percent}%)
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip label={checklist.status === 'finalized' ? 'Finalisiert' : (checklist.status === 'completed' ? 'Vollständig' : 'In Bearbeitung')} color={checklist.status === 'finalized' ? 'success' : (checklist.status === 'completed' ? 'primary' : 'warning')} />
          </Stack>
        </Stack>
      </Paper>

      {sections.map((section) => {
        const sectionProgress = getSectionProgress(section);

        return (
        <Accordion key={section.sectionKey} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700 }}>
              {section.sectionTitle} ({sectionProgress.completedCount}/{sectionProgress.totalCount})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {(section.items || []).map((item) => (
                <Paper
                  key={item.id || `${section.sectionKey}-${item.sortOrder}`}
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    cursor: isFinalized ? 'default' : 'pointer',
                    '&:hover': isFinalized ? undefined : { borderColor: 'primary.main' },
                  }}
                  onClick={isFinalized ? undefined : () => setEditingItem(item)}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Einweisung durch: {item.instructorFullName || '-'} | Datum: {item.completedDate ? formatDate(item.completedDate) : '-'}
                      </Typography>
                    </Box>
                    <Chip
                      label={item.isCompleted ? 'Erledigt' : 'Offen'}
                      color={item.isCompleted ? 'success' : 'default'}
                      size="small"
                    />
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
        );
      })}

      <ChecklistEditDialog
        open={Boolean(editingItem)}
        item={editingItem}
        instructors={instructors}
        onClose={() => setEditingItem(null)}
        onSave={handleSaveItem}
        saving={saving}
      />

      <Dialog open={completionDialogOpen} onClose={handleCloseCompletionDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Einarbeitung vollständig</DialogTitle>
        <DialogContent>
          <Alert severity="success" icon={<TaskAltIcon />}>
            Gratuliere! Du hast alle Einarbeitungsthemen nach deinem Grundlehrgang absolviert. Weiter so!
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCompletionDialog} variant="contained">Schließen</Button>
        </DialogActions>
      </Dialog>

      {isFinalized && !canFinalize ? (
        <Alert severity="info">Der Laufzettel wurde finalisiert und ist nur noch als Nachweis hinterlegt.</Alert>
      ) : null}
    </Stack>
  );
}

function AdminChecklistPanel() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [entries, setEntries] = useState([]);
  const [selectedChecklistId, setSelectedChecklistId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getSrhtChecklistAdminOpenList();
      const nextEntries = Array.isArray(response.data?.entries) ? response.data.entries : [];
      setEntries(nextEntries);
      if (nextEntries.length === 0) {
        setSelectedDetail(null);
      }

      setSelectedChecklistId((currentChecklistId) => {
        if (nextEntries.length === 0) {
          return null;
        }
        if (!nextEntries.some((entry) => Number(entry.checklistId) === Number(currentChecklistId))) {
          return nextEntries[0].checklistId;
        }
        return currentChecklistId;
      });
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Die Laufzettel-Übersicht konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (!selectedChecklistId) {
      setSelectedDetail(null);
      return;
    }

    let active = true;
    const loadDetail = async () => {
      try {
        const response = await getSrhtChecklistAdminDetail(selectedChecklistId);
        if (active) {
          setSelectedDetail(response.data || null);
        }
      } catch {
        if (active) {
          setSelectedDetail(null);
        }
      }
    };

    loadDetail();
    return () => {
      active = false;
    };
  }, [selectedChecklistId]);

  const handleFinalize = async () => {
    if (!selectedChecklistId) {
      return;
    }

    setBusy(true);
    setError('');
    try {
      await finalizeSrhtChecklist(selectedChecklistId);
      setNotice('Laufzettel wurde finalisiert.');
      await loadOverview();
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Der Laufzettel konnte nicht finalisiert werden.');
    } finally {
      setBusy(false);
    }
  };

  const handleExportPdf = async () => {
    if (!selectedChecklistId) {
      return;
    }

    setBusy(true);
    setError('');
    try {
      const response = await exportSrhtChecklistPdf(selectedChecklistId);
      downloadPdfResponse(response, 'Laufzettel.pdf');
      setNotice('Laufzettel-PDF wurde erstellt.');
    } catch (requestError) {
      setError(await extractPdfErrorMessage(requestError, 'Der Laufzettel-PDF-Export ist fehlgeschlagen.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
      {notice ? <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert> : null}

      {loading ? <Alert severity="info">Laufzettel-Übersicht wird geladen...</Alert> : null}

      {!loading && entries.length === 0 ? (
        <Alert severity="info">Aktuell gibt es keine offenen Laufzettel.</Alert>
      ) : null}

      {!loading && entries.length > 0 ? (
        <Grid container spacing={1.25}>
          {entries.map((entry) => {
            const isSelected = Number(selectedChecklistId) === Number(entry.checklistId);
            const statusLabel = entry.status === 'completed' ? 'Vollständig' : 'In Bearbeitung';
            const statusColor = entry.status === 'completed' ? 'success' : 'warning';

            return (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={entry.checklistId}>
                <Card
                  variant="outlined"
                  onClick={() => setSelectedChecklistId(isSelected ? null : entry.checklistId)}
                  sx={{
                    cursor: 'pointer',
                    height: '100%',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    boxShadow: isSelected ? 2 : 0,
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{entry.fullName}</Typography>
                        <Chip label={statusLabel} size="small" color={statusColor} />
                      </Stack>

                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }} color="text.primary">
                        {entry.progress.percent}%
                      </Typography>

                      <LinearProgress
                        variant="determinate"
                        value={Math.max(0, Math.min(100, Number(entry.progress.percent || 0)))}
                        sx={{ height: 8, borderRadius: 999 }}
                        color={entry.progress.isComplete ? 'success' : 'primary'}
                      />

                      <Typography variant="caption" color="text.secondary">
                        {entry.progress.completedCount}/{entry.progress.totalCount} Themen
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : null}

      {selectedDetail?.checklist ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.25}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedDetail.checklist.fullName}</Typography>
            <Typography variant="body2" color="text.secondary">
              Fortschritt: {selectedDetail.progress.completedCount}/{selectedDetail.progress.totalCount} ({selectedDetail.progress.percent}%)
            </Typography>

            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, Number(selectedDetail.progress.percent || 0)))}
              sx={{ height: 10, borderRadius: 999, maxWidth: 360 }}
              color={selectedDetail.progress.isComplete ? 'success' : 'primary'}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf} disabled={busy}>
                PDF exportieren
              </Button>
              <Button variant="contained" color="success" onClick={handleFinalize} disabled={busy || !selectedDetail.progress.isComplete}>
                Laufzettel finalisieren
              </Button>
            </Stack>

            {(selectedDetail.sections || []).map((section) => {
              const sectionProgress = getSectionProgress(section);

              return (
              <Accordion key={section.sectionKey} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 700 }}>
                    {section.sectionTitle} ({sectionProgress.completedCount}/{sectionProgress.totalCount})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {(section.items || []).map((item) => (
                      <Paper
                        key={item.id || `${section.sectionKey}-${item.sortOrder}`}
                        variant="outlined"
                        sx={{ p: 1.25 }}
                      >
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.title}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Einweisung durch: {item.instructorFullName || '-'} | Datum: {item.completedDate ? formatDate(item.completedDate) : '-'}
                            </Typography>
                          </Box>
                          <Chip
                            label={item.isCompleted ? 'Erledigt' : 'Offen'}
                            color={item.isCompleted ? 'success' : 'default'}
                            size="small"
                          />
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
              );
            })}

            {!selectedDetail.progress.isComplete ? (
              <Alert severity="warning">Finalisierung ist erst möglich, wenn alle Themen vollständig ausgefüllt sind.</Alert>
            ) : null}
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
}

function SrhtChecklistPage() {
  const { user } = useAuth();
  const manageMode = useMemo(() => canManageSrhtChecklist(user), [user]);

  return (
    <Box sx={{ px: { xs: 1, sm: 3 }, py: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Laufzettel</Typography>
        </Box>

        <MyChecklistPanel canFinalize={manageMode} />

        {manageMode ? (
          <AdminChecklistPanel />
        ) : null}
      </Stack>
    </Box>
  );
}

export default SrhtChecklistPage;
